import { ActionTypes } from "@/actions";
import { fetchCollection, resolveComponentBySlug } from "@/app/api/tableData";
import { serializeErrorForDiagnostics } from "@/utils/errorMessage";
import { call, delay, put, race, select, takeEvery, takeLatest } from "redux-saga/effects";

/** Prevents indefinite loading if fetch hangs (no response / stalled connection). */
const TABLE_FETCH_TIMEOUT_MS = 45_000;

function uniqueLoadingKeys(...keys: (string | null | undefined)[]): string[] {
    return [...new Set(keys.filter((k): k is string => typeof k === 'string' && k.length > 0))];
}

function isValidTableMeta(meta: unknown): meta is Record<string, unknown> {
    return Boolean(meta && typeof meta === 'object' && !Array.isArray(meta) && Object.keys(meta as object).length > 0);
}

/** `{...new Error()}` is `{}` (message/stack non-enumerable); preserve details for TABLE_FAILURE. */
function spreadableTableErrorFields(error: unknown): Record<string, unknown> {
    if (error instanceof Error) {
        const e = error as Error & {
            status?: number
            statusText?: string
            type?: string
            slug?: string
            code?: string
        };
        const rec: Record<string, unknown> = {
            message: e.message,
            name: e.name,
        };
        if (typeof e.status === 'number') rec.status = e.status;
        if (typeof e.statusText === 'string') rec.statusText = e.statusText;
        if (typeof e.type === 'string') rec.type = e.type;
        if (typeof e.slug === 'string') rec.slug = e.slug;
        if (typeof e.code === 'string') rec.code = e.code;
        return rec;
    }
    if (error && typeof error === 'object' && !Array.isArray(error)) {
        return { ...(error as Record<string, unknown>) };
    }
    return { message: String(error ?? 'Unknown error') };
}

const workerTableRequest = function* (action: any): Generator<any, void, any> {
    const tableKeyForFailure = action.payload?.tableKey || action.payload?.slugName;
    const requestTableKey = typeof action.payload?.tableKey === 'string' ? action.payload.tableKey : undefined;
    const requestSlugName = typeof action.payload?.slugName === 'string' ? action.payload.slugName : undefined;
    try {
        const { response } = yield race({
            response: call(fetchCollection, action.payload),
            _: delay(TABLE_FETCH_TIMEOUT_MS),
        }) as { response?: Awaited<ReturnType<typeof fetchCollection>> };

        if (response === undefined) {
            yield put({
                type: ActionTypes.TABLE_FAILURE,
                payload: {
                    type: 'TABLE_FETCH_TIMEOUT',
                    message: 'Table load timed out',
                    tableKey: requestTableKey || tableKeyForFailure,
                    slug: requestSlugName || tableKeyForFailure,
                },
            });
            return;
        }
        if (!isValidTableMeta(response?.meta)) {
            yield put({
                type: ActionTypes.TABLE_FAILURE,
                payload: {
                    type: 'TABLE_META_INVALID',
                    message: 'Collection metadata missing or invalid',
                    tableKey: requestTableKey || tableKeyForFailure,
                    slug: requestSlugName || tableKeyForFailure,
                },
            });
            return;
        }

        const meta = response.meta && typeof response.meta === 'object' ? response.meta as { tableKey?: string; slug?: string } : null;
        const clearLoadingForKeys = uniqueLoadingKeys(
            requestTableKey,
            requestSlugName,
            tableKeyForFailure,
            typeof response.slug === 'string' ? response.slug : undefined,
            meta?.tableKey,
            typeof meta?.slug === 'string' ? meta.slug : undefined,
        );

        const storageSlug =
            requestTableKey ||
            (typeof meta?.tableKey === 'string' ? meta.tableKey : undefined) ||
            (typeof response.slug === 'string' ? response.slug : undefined) ||
            requestSlugName ||
            tableKeyForFailure;

        yield put({
            type: ActionTypes.TABLE_SUCCESS,
            payload: {
                slug: storageSlug,
                data: response.data,
                pagination: response.pagination,
                clearLoadingForKeys,
            }
        });
        const fields = Array.isArray(response.meta.fields) ? response.meta.fields : [];
        const withRelationFields = fields.filter(
            (field: any) => field && Object.prototype.hasOwnProperty.call(field, 'fields')
        );
        if (withRelationFields.length > 0 && response.meta?.slug) {
            yield put({
                type: ActionTypes.POPULATE_RELATION_FIELDS,
                payload: {
                    slug: response.meta.slug,
                    fieldsToPopulate: withRelationFields,
                    user: action.payload.user
                }
            });
        }
        yield put({ type: ActionTypes.TABLE_META_SUCCESS, payload: response.meta });
    } catch (error: any) {
        // For 404 errors on mongo collections, set the meta so users can add items
        if (error?.type === 'COLLECTION_NOT_FOUND_404' && error?.componentMeta) {
            yield put({ type: ActionTypes.TABLE_META_SUCCESS, payload: error.componentMeta });
            const slugForEmptyTable =
                error.componentMeta?.tableKey || tableKeyForFailure || error.slug;
            const clear404 = uniqueLoadingKeys(
                requestTableKey,
                requestSlugName,
                tableKeyForFailure,
                typeof slugForEmptyTable === 'string' ? slugForEmptyTable : undefined,
                error.componentMeta?.tableKey,
                typeof error.componentMeta?.slug === 'string' ? error.componentMeta.slug : undefined,
            );
            yield put({
                type: ActionTypes.TABLE_SUCCESS,
                payload: { slug: slugForEmptyTable, data: [], pagination: undefined, clearLoadingForKeys: clear404 },
            });
            return;
        }

        const errSlug =
            typeof error === 'object' && error !== null && typeof (error as { slug?: unknown }).slug === 'string'
                ? (error as { slug: string }).slug
                : undefined;
        const failurePayload = {
            ...spreadableTableErrorFields(error),
            tableKey: requestTableKey || tableKeyForFailure,
            slug: requestSlugName ?? errSlug ?? tableKeyForFailure,
        };
        yield put({ type: ActionTypes.TABLE_FAILURE, payload: failurePayload });
        const errorMessage = error?.message || error?.toString() || '';
        const isAuthError = errorMessage.includes('auth') || errorMessage.includes('unauthorized') || errorMessage.includes('401');

        if (isAuthError) {
            yield put({ type: ActionTypes.NAVIGATE_ROOT });
        }
    }
}

const workerPopulateRelationFields = function* (action: any): Generator<any, void, any> {
    const fieldsToPopulate: any[] = Array.isArray(action.payload?.fieldsToPopulate)
        ? action.payload.fieldsToPopulate
        : [];
    if (fieldsToPopulate.length === 0) {
        return;
    }

    const sourceSlug = action?.payload?.slug || 'unknown-slug';
    const existingMeta: any[] = (yield select((s: any) => s.table?.meta)) ?? [];
    const seenRelationSlugs = new Set<string>();

    for (const relationField of fieldsToPopulate) {
        try {
            const fieldValue = relationField.value;
            const sanitizedValue =
                typeof fieldValue === 'string' ? fieldValue.replace(/[!]+$/, '').trim() : fieldValue;
            if (!sanitizedValue || typeof sanitizedValue !== 'string') {
                continue;
            }
            if (seenRelationSlugs.has(sanitizedValue)) {
                continue;
            }
            seenRelationSlugs.add(sanitizedValue);

            const alreadyLoaded = existingMeta.some(
                (m: any) =>
                    m &&
                    (m.slug === sanitizedValue ||
                        (typeof m.tableKey === 'string' && m.tableKey.endsWith(`/${sanitizedValue}`)))
            );
            if (alreadyLoaded) {
                continue;
            }

            const componentMeta = yield call(resolveComponentBySlug, sanitizedValue, action.payload.user);
            if (componentMeta) {
                yield put({ type: ActionTypes.TABLE_META_SUCCESS, payload: componentMeta });
            }
        } catch (error: any) {
            // Don't log errors for relation fields - they're optional and failures shouldn't block the page
            const errorMessage = error?.message || error?.toString() || '';
            const fieldName = relationField?.name || relationField?.label || relationField?.value || 'unknown-field';

            if (errorMessage.includes('not found')) {
                console.warn(
                    `workerPopulateRelationFields: not found for field "${fieldName}" (${relationField?.value}) on "${sourceSlug}": ${errorMessage}`
                );
            } else {
                console.error(
                    `Failed to populate relation field "${fieldName}" (value: "${relationField?.value}") for slug "${sourceSlug}"`,
                    error
                );
            }
        }
    }
}

const workerTableFailure = function* (action: any) {
    const p = action.payload;
    const short =
        (p && typeof p === 'object' && (p.message || p.type)) ?
            `${p.type ? String(p.type) + ': ' : ''}${String(p.message || 'failed')}` :
            String(p ?? 'Unknown error');
    console.error('Table load failed:', short);
    console.error('Table load failed (payload diagnostics):', serializeErrorForDiagnostics(p));
}

export function* watcherTableRequest() {
    yield takeLatest(ActionTypes.TABLE_REQUEST, workerTableRequest);
    yield takeEvery(ActionTypes.TABLE_FAILURE, workerTableFailure);
    yield takeEvery(ActionTypes.POPULATE_RELATION_FIELDS, workerPopulateRelationFields);
}